import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight, Loader2, CheckCircle2, Clock, AlertCircle } from "lucide-react";

const STATUS_STYLE = {
  active:    { color: "#22c55e", label: "Active",             icon: CheckCircle2 },
  approved:  { color: "#d4af37", label: "Approved",           icon: CheckCircle2 },
  submitted: { color: "#3b82f6", label: "Submitted",          icon: Clock },
  ready:     { color: "#a1a1aa", label: "Ready to file",      icon: Clock },
  pending:   { color: "#71717a", label: "Pending intake",     icon: Clock },
  blocked:   { color: "#ef4444", label: "Needs your input",   icon: AlertCircle },
  not_needed:{ color: "#3f3f46", label: "Not selected",       icon: null },
};

export default function StructureStatusCard() {
  const [state, setState] = useState({ loading: true, exists: false, data: null });
  const [score, setScore] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: intake }, { data: scoreData }] = await Promise.all([
          api.get("/structure/my-intake"),
          api.get("/structure/my-success-score"),
        ]);
        setState({ loading: false, exists: !!intake.exists, data: intake });
        setScore(scoreData);
      } catch (e) {
        setState({ loading: false, exists: false, data: null });
      }
    })();
  }, []);

  if (state.loading) {
    return (
      <div className="border border-white/10 bg-[#0a0a0a] p-8 flex items-center justify-center min-h-[200px]">
        <Loader2 className="animate-spin text-[var(--primary)]" size={20} />
      </div>
    );
  }

  if (!state.exists) {
    return (
      <div className="border border-[var(--primary)]/40 bg-gradient-to-br from-[var(--primary)]/5 to-transparent p-8 md:p-10" data-testid="structure-cta">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--primary)] mb-3">
          // Structure Setup — Ready to begin
        </div>
        <h3 className="font-heading font-black text-2xl md:text-3xl tracking-tight mb-3">
          Get every royalty source registered.
        </h3>
        <p className="text-zinc-400 text-sm leading-relaxed mb-6 max-w-2xl">
          Complete a single intake and we&rsquo;ll auto-populate your registrations
          across BMI, ASCAP, The MLC, SoundExchange, Songtrust, United Masters,
          Content ID and more. You only touch it once — we file everything else.
        </p>
        <Link to="/onboarding" className="btn-primary" data-testid="structure-start-cta">
          Start onboarding <ArrowUpRight size={14} />
        </Link>
      </div>
    );
  }

  const manifest = (state.data.manifest || []).filter((m) => m.status !== "not_needed");
  const active = manifest.filter((m) => m.status === "active").length;
  const submitted = manifest.filter((m) => m.status === "submitted" || m.status === "approved").length;
  const ready = manifest.filter((m) => m.status === "ready" || m.status === "pending").length;
  const totalSelected = manifest.length;
  const progressPct = totalSelected > 0 ? Math.round((active / totalSelected) * 100) : 0;

  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-8 md:p-10" data-testid="structure-status">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--primary)] mb-2">
            // Structure Registrations
          </div>
          <h3 className="font-heading font-black text-2xl md:text-3xl tracking-tight">
            {active} of {totalSelected} active
          </h3>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-1">
            Structure Success Score
          </div>
          <div className="flex items-baseline gap-2 justify-end" data-testid="structure-success-score">
            <div className="font-heading font-black text-4xl md:text-5xl text-[var(--primary)] leading-none">{score?.score ?? progressPct}</div>
            <div className="font-heading font-black text-xl text-white/40">/100</div>
            {score?.grade && (
              <div className="ml-2 border border-[var(--primary)]/40 bg-[var(--primary)]/5 px-2 py-1 font-mono text-[10px] tracking-widest uppercase text-[var(--primary)]">
                {score.grade}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-white/5 mb-8 overflow-hidden">
        <div
          className="h-full bg-[var(--primary)] transition-all duration-500"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Counts */}
      <div className="grid grid-cols-3 gap-3 mb-8 text-center">
        <div className="border border-white/10 p-3">
          <div className="font-heading font-black text-2xl text-white">{active}</div>
          <div className="font-mono text-[9px] tracking-widest uppercase text-zinc-500 mt-1">Active</div>
        </div>
        <div className="border border-white/10 p-3">
          <div className="font-heading font-black text-2xl text-white">{submitted}</div>
          <div className="font-mono text-[9px] tracking-widest uppercase text-zinc-500 mt-1">In progress</div>
        </div>
        <div className="border border-white/10 p-3">
          <div className="font-heading font-black text-2xl text-white">{ready}</div>
          <div className="font-mono text-[9px] tracking-widest uppercase text-zinc-500 mt-1">Queued</div>
        </div>
      </div>

      {/* Manifest list */}
      <div className="space-y-2" data-testid="structure-manifest">
        {manifest.map((row) => {
          const s = STATUS_STYLE[row.status] || STATUS_STYLE.pending;
          const Icon = s.icon;
          return (
            <div key={row.key} className="flex items-center justify-between border border-white/5 bg-black/30 px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                {Icon && <Icon size={16} style={{ color: s.color }} className="flex-shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{row.name}</div>
                  <div className="font-mono text-[9px] tracking-widest uppercase text-zinc-600 mt-0.5">
                    {row.category}
                    {row.reference_id && ` · ${row.reference_id}`}
                  </div>
                </div>
              </div>
              <div className="font-mono text-[10px] tracking-widest uppercase flex-shrink-0 ml-3" style={{ color: s.color }}>
                {s.label}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 pt-6 border-t border-white/5 text-center">
        <Link to="/onboarding" className="font-mono text-[10px] tracking-widest uppercase text-zinc-400 hover:text-[var(--primary)] transition-colors">
          Update intake info <ArrowUpRight size={12} className="inline" />
        </Link>
      </div>
    </div>
  );
}
